import { useState, useEffect } from 'react';
import { ServiceAttributeSummaryData } from '../models/PulseData';
import { FetchState } from '../models/commonTypes';
import { CustomerInfo, getServicesForGroupId } from '../models/CustomerData';
import { fetchServicesGroupAttributeSummary } from '../services/pulseService';
import { useCustomer } from '../contexts/CustomerContext';
import { TimeSliceWindow } from '@/models/TimeSlice';
import logger from '../utils/logger';

interface UsePulseGroupAttributeSummaryDataProps {
  selectedCustomer: CustomerInfo | null;
  selectedServicesGroupId: string | null;
  selectedGroupAttribute: string | null; // The attribute selected for the group summary
  timeSliceWindow: TimeSliceWindow;
}

export const usePulseGroupAttributeSummaryData = ({
  selectedCustomer,
  selectedServicesGroupId,
  selectedGroupAttribute,
  timeSliceWindow,
}: UsePulseGroupAttributeSummaryDataProps): FetchState<ServiceAttributeSummaryData> => {
  const [summaryState, setSummaryState] = useState<FetchState<ServiceAttributeSummaryData>>({
    data: null,
    isLoading: false, // Initially not loading until all params are present
    error: null,
  });

  const { selectedApiKey} = useCustomer();

  useEffect(() => {
    if (!selectedCustomer || !selectedServicesGroupId || !selectedGroupAttribute) {
      setSummaryState({ data: null, isLoading: false, error: null }); // Reset if params are missing
      return;
    }

    const fetchSummary = async () => {
      setSummaryState({ data: null, isLoading: true, error: null });

      try {
        const params = {
          apiKey: selectedApiKey,
          customer: selectedCustomer.id,
          servicesList: getServicesForGroupId( selectedCustomer, selectedServicesGroupId),
          attribute: selectedGroupAttribute,
          startDate: timeSliceWindow.startTime,
          endDate: timeSliceWindow.endTime,
          timeIncrement: timeSliceWindow.incrementMinutes,
          token: null, // Assuming token is handled by apiClient or not needed for this specific call
        };

        const data = await fetchServicesGroupAttributeSummary(params);

        if (!data || data.message !== 'success') {
          throw new Error(
            `Fetching ServicesGroupAttribute summary for '${selectedGroupAttribute}' failed: ${data?.message || 'Unknown error'}`,
          );
        }
        setSummaryState({ data, isLoading: false, error: null });
        logger.info(`Successfully fetched group attribute summary for: ${selectedGroupAttribute}`);
      } catch (err) {
        logger.error(`Failed to fetch group attribute summary for ${selectedGroupAttribute}:`, err);
        const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
        setSummaryState({ data: null, isLoading: false, error: errorMessage });
      }
    };

    fetchSummary();
  }, [selectedCustomer, selectedApiKey, selectedServicesGroupId, selectedGroupAttribute, timeSliceWindow]);

  return summaryState;
};